import { Card } from './ui';
import { calendarDate, calendarSortKey } from '../lib/calendarDate';
import type { HistoryRow } from './DailyClicksHistory';

interface Props {
  rows: HistoryRow[];
  eventTypes?: string[];
  selectedRows?: HistoryRow[];
  loading?: boolean;
}

const standardEvents = ['home', 'search', 'category', 'product', 'cart', 'purchase'];
const dedicatedColumns = new Map<string, keyof HistoryRow>([
  ['home', 'pageviews_home'], ['product', 'pageviews_product'],
  ['cart', 'pageviews_cart'], ['purchase', 'pageviews_purchase'],
]);
const cellClass = 'px-4 py-2.5 text-right whitespace-nowrap';
const dateValue = (row: HistoryRow) => row.date || row.id_yyyymmdd || '';
const dateKey = (row: HistoryRow) => String(calendarSortKey(dateValue(row)) || dateValue(row));
const formatCount = (value: number | null) => value == null ? '—' : value.toLocaleString();

function recordedCount(value: unknown): number | null {
  if (typeof value !== 'number' && typeof value !== 'string' || typeof value === 'string' && !value.trim()) return null;
  const count = Number(value);
  return Number.isSafeInteger(count) && count >= 0 ? count : null;
}

function customCounts(value: unknown): Record<string, unknown> | null {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

// The sync writes explicit zeroes for configured custom events. A missing key
// in a historical row is therefore unknown, rather than a recorded zero.
function eventCount(row: HistoryRow, event: string, custom: Record<string, unknown> | null): number | null {
  if (row.delivery_missing) return null;
  const column = dedicatedColumns.get(event);
  if (column) return recordedCount(row[column]);
  return custom && Object.hasOwn(custom, event) ? recordedCount(custom[event]) : null;
}

export function DailyPageviewsHistory({ rows, eventTypes = [], selectedRows = [], loading = false }: Props) {
  const availableRows = (loading ? [] : rows).map(row => ({ row, custom: customCounts(row.custom_pageviews) }));
  const events = [...new Set([
    ...standardEvents,
    ...eventTypes,
    ...availableRows.flatMap(({ custom }) => Object.keys(custom || {})),
  ].filter(event => typeof event === 'string' && event.trim()))];
  const labels = ['Total Pageviews', ...events.map(event => standardEvents.includes(event)
    ? event.charAt(0).toUpperCase() + event.slice(1) : event)];
  const days = availableRows.map(({ row, custom }) => ({
    key: dateKey(row), date: calendarDate(dateValue(row)),
    counts: [row.delivery_missing ? null : recordedCount(row.pageviews), ...events.map(event => eventCount(row, event, custom))],
  }));
  const selectedKeys = new Set(selectedRows.map(dateKey));
  const selectedDays = days.filter(day => selectedKeys.has(day.key));
  const hasMissing = days.some(day => day.counts.some(count => count == null));

  const summaryCells = (summaryDays: typeof days) => labels.map((label, index) => {
    const counts = summaryDays.flatMap(day => day.counts[index] == null ? [] : [day.counts[index]!]);
    const total = counts.length ? counts.reduce((sum, count) => sum + count, 0) : null;
    const partial = counts.length > 0 && counts.length < summaryDays.length;
    return <td key={index} className={cellClass} title={`${label}: ${counts.length}/${summaryDays.length} days with a recorded count.${partial ? ' Partial total; missing counts are excluded.' : ''}`}>
      {formatCount(total)}{partial ? '*' : ''}
    </td>;
  });

  return <Card className="overflow-hidden mb-6">
    <div className="px-4 py-3 border-b border-border">
      <h2 className="text-sm font-semibold text-ink-900">Daily Pageviews by Event</h2>
      <p className="text-xs text-slate-500 mt-1">Same period as Daily Clicks History · select days above to see their subtotal here</p>
    </div>
    <div className="max-h-[70vh] overflow-auto" data-testid="daily-pageviews-history" role="region" aria-label="Daily pageviews scroll area" tabIndex={0} aria-busy={loading}>
      <table className="w-full text-sm tabular-nums" aria-label="Daily Pageviews by Event">
        <thead className="sticky top-0 z-10 bg-surface shadow-sm">
          <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-surface-muted">
            <th scope="col" className="px-4 py-2.5 font-medium whitespace-nowrap">Date</th>
            {labels.map((label, index) => <th key={index} scope="col" className="px-4 py-2.5 font-medium text-right whitespace-nowrap">{label}</th>)}
          </tr>
          {selectedDays.length > 0 && <tr data-testid="pageviews-selection-summary" className="bg-brand-50 text-ink-900 font-semibold border-y border-brand-100">
            <td className="px-4 py-3 whitespace-nowrap"><span aria-live="polite">Selected ({selectedDays.length})</span></td>
            {summaryCells(selectedDays)}
          </tr>}
        </thead>
        <tbody className="divide-y divide-border">
          {days.map((day, index) => <tr key={day.key || index} data-testid="pageviews-day" className={`text-slate-700 ${selectedKeys.has(day.key) ? 'bg-brand-50' : 'hover:bg-surface-muted'}`}>
            <th scope="row" className="px-4 py-2.5 text-left font-normal text-slate-600 whitespace-nowrap">{day.date || '—'}</th>
            {day.counts.map((count, column) => <td key={column} className={cellClass} title={count == null ? 'No recorded count for this day.' : undefined}>{formatCount(count)}</td>)}
          </tr>)}
          {!days.length && <tr><td colSpan={labels.length + 1} className="px-4 py-8 text-center text-slate-400">{loading ? 'Loading…' : 'No data in this period.'}</td></tr>}
        </tbody>
        {days.length > 0 && <tfoot>
          <tr data-testid="pageviews-total" className="bg-surface-muted font-semibold text-ink-900 border-t-2 border-border">
            <td className="px-4 py-3 whitespace-nowrap">Total</td>{summaryCells(days)}
          </tr>
        </tfoot>}
      </table>
    </div>
    <p className="px-4 py-3 text-xs text-slate-500 border-t border-border">
      Total Pageviews uses the recorded daily total; the event breakdown may not add up to it. — means no recorded count.
      {hasMissing && ' * Totals include available counts only.'}
    </p>
  </Card>;
}
